/**
 * Punto de entrada del panel.
 *
 * ⚠️ REACT ROUTER 8 — LA TRAMPA MÁS FÁCIL DE PISAR:
 *
 *   · `react-router-dom` NO EXISTE en la v8. Su última publicación es la
 *     7.18.1 y NO HAY NINGUNA versión 8.x. Todo se importa de `react-router`.
 *
 *   · EXCEPTO `RouterProvider`, que viene de `react-router/dom`.
 *     Es la única excepción, y es justo la que todo el mundo se come.
 *
 * Rutas: `/login` (pública) y todo lo demás detrás de la guardia + layout.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, Navigate, useParams } from 'react-router'
import { RouterProvider } from 'react-router/dom' // ← NO de 'react-router'

import { AparienciaRoot } from './apariencia'
import { RequireAuth } from './auth'
import { Layout } from './Layout'
import { VerComoProvider } from './ver-como'
import { AcceptInvite } from './routes/accept-invite'
import { CalendarioNegocio } from './routes/calendario'
import { ContadorDetalle } from './routes/contador-detalle'
import { ConversationDetail } from './routes/conversation-detail'
import { Conversations } from './routes/conversations'
import { CancelacionesNegocio } from './routes/cancelaciones'
import { DashboardNegocio } from './routes/dashboard-negocio'
import { ForgotPassword } from './routes/forgot-password'
import { Health } from './routes/health'
import { Login } from './routes/login'
import { ResetPassword } from './routes/reset-password'
import { SinNegocio } from './routes/sin-negocio'
import { AltaCuarentena } from './routes/altas/cuarentena'
import { AltasIndex } from './routes/altas/index'
import { AltaNueva } from './routes/altas/nueva'
import { Borradores } from './routes/altas/borradores'
import { Plantillas } from './routes/altas/plantillas'
import { AltaPrevisualizar } from './routes/altas/previsualizar'
import { AltaProgreso } from './routes/altas/progreso'
import { InformacionNegocio } from './routes/negocio/informacion'
import { InvitarNegocio } from './routes/negocios/invitar'
import { AuditoriaNegocio } from './routes/negocio/auditoria'
import { SupervisorNegocio } from './routes/negocio/supervisor'
import { ElevadasNegocio } from './routes/negocio/elevadas'
import { EsperaNegocio } from './routes/negocio/espera'
import { ClienteFichaNegocio } from './routes/negocio/cliente-ficha'
import { ClientesNegocio } from './routes/negocio/clientes'
import { CatalogoNegocio } from './routes/negocio/catalogo'
import { AccesosAdmin } from './routes/admin/accesos'
import { UsuariosAdmin } from './routes/admin/usuarios'
import { AdminRetencion } from './routes/admin/retencion'
import { McpFacebook } from './routes/admin/mcp-facebook'
import { McpFacebookOauth } from './routes/admin/mcp-facebook-oauth'
import { WhatsappAdmin } from './routes/admin/whatsapp'
import { Perfil } from './routes/perfil'
import { RaizPorRol } from './routes/raiz-por-rol'
import './styles.css'

function RedirigirHorariosACatalogo() {
    const { accountId } = useParams<{ accountId: string }>()
    return <Navigate to={`/business/${accountId}/catalogo`} replace />
}

function RedirigirContactoAInfo() {
    const { accountId } = useParams<{ accountId: string }>()
    return <Navigate to={`/business/${accountId}/info`} replace />
}

/**
 * TanStack Query es la caché; SSE es la frescura.
 *
 * `refetchInterval` está PROHIBIDO en todo el proyecto (FR-012): sería el
 * navegador sondeando, que es exactamente lo que la constitución prohíbe y el
 * patrón que hundió a P05. Y no es un acuerdo entre caballeros: hay una regla
 * de ESLint que rompe el build si alguien lo escribe.
 */
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: 2,
        },
    },
})

const router = createBrowserRouter([
    { path: '/login', element: <Login /> },
    { path: '/accept-invite', element: <AcceptInvite /> },
    { path: '/forgot-password', element: <ForgotPassword /> },
    { path: '/reset-password', element: <ResetPassword /> },
    {
        element: <RequireAuth />,
        children: [
            {
                element: <Layout />,
                children: [
                    // NIVEL 1 — solo admin; cliente redirige (FR-005).
                    { index: true, element: <RaizPorRol /> },
                    { path: 'sin-negocio', element: <SinNegocio /> },
                    // Nivel 2 — el dashboard del negocio (lo que ve el cliente).
                    { path: 'business/:accountId', element: <DashboardNegocio /> },
                    { path: 'business/:accountId/counter/:nombre', element: <ContadorDetalle /> },
                    { path: 'business/:accountId/cancelaciones', element: <CancelacionesNegocio /> },
                    // El calendario es NIVEL 2: cuelga del negocio, no de la portada.
                    // EasyAppointments no es multi-tenant — un calendario sin
                    // negocio no tiene a quién preguntarle.
                    { path: 'business/:accountId/calendar', element: <CalendarioNegocio /> },
                    { path: 'business/:accountId/horarios', element: <RedirigirHorariosACatalogo /> },
                    { path: 'business/:accountId/clientes', element: <ClientesNegocio /> },
                    { path: 'business/:accountId/clientes/:contactId', element: <ClienteFichaNegocio /> },
                    { path: 'business/:accountId/auditoria', element: <AuditoriaNegocio /> },
                    { path: 'business/:accountId/supervisor', element: <SupervisorNegocio /> },
                    { path: 'business/:accountId/espera', element: <EsperaNegocio /> },
                    { path: 'business/:accountId/elevadas', element: <ElevadasNegocio /> },
                    { path: 'business/:accountId/catalogo', element: <CatalogoNegocio /> },
                    { path: 'business/:accountId/info', element: <InformacionNegocio /> },
                    { path: 'business/:accountId/contacto', element: <RedirigirContactoAInfo /> },
                    { path: 'business/:accountId/invitar', element: <InvitarNegocio /> },
                    // NIVEL 3 — la consola. Deja de ser la portada y pasa a colgar
                    // DEL NEGOCIO (FR-037): la herramienta de depuración de ESE
                    // negocio, no una vista global. El ámbito se ve en la barra de
                    // direcciones, que es donde el operador lo mira cuando salta
                    // entre negocios. Solo `admin` (FR-038).
                    { path: 'business/:accountId/console', element: <Conversations /> },
                    { path: 'business/:accountId/console/:id', element: <ConversationDetail /> },
                    { path: 'health', element: <Health /> },
                    // Alta de negocios (solo admin; el backend responde 404 al resto).
                    { path: 'altas/nueva', element: <AltaNueva /> },
                    { path: 'altas/previsualizar', element: <AltaPrevisualizar /> },
                    { path: 'altas/cuarentena', element: <AltaCuarentena /> },
                    { path: 'altas/plantillas', element: <Plantillas /> },
                    { path: 'altas/borradores', element: <Borradores /> },
                    { path: 'altas', element: <AltasIndex /> },
                    { path: 'altas/:id', element: <AltaProgreso /> },
                    // Retención / supresión (008) — solo admin; API 404 al resto.
                    { path: 'retencion', element: <AdminRetencion /> },
                    { path: 'mcp-facebook/oauth', element: <McpFacebookOauth /> },
                    { path: 'mcp-facebook', element: <McpFacebook /> },
                    { path: 'whatsapp', element: <WhatsappAdmin /> },
                    { path: 'accesos', element: <AccesosAdmin /> },
                    { path: 'usuarios', element: <UsuariosAdmin /> },
                    { path: 'usuarios/nuevo', element: <UsuariosAdmin /> },
                    { path: 'usuarios/:id', element: <UsuariosAdmin /> },
                    { path: 'perfil', element: <Perfil /> },
                ],
            },
        ],
    },
])

createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <QueryClientProvider client={queryClient}>
            <AparienciaRoot>
                <VerComoProvider>
                    <RouterProvider router={router} />
                </VerComoProvider>
            </AparienciaRoot>
        </QueryClientProvider>
    </StrictMode>,
)
